"use client"

import React, { ReactNode, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    colors,
    useTheme,
    Backdrop,
} from '@mui/material';
import { FaXmark } from 'react-icons/fa6';

interface OffCanvasProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    width?: number;
}

const OffCanvas: React.FC<OffCanvasProps> = ({
    open,
    onClose,
    title,
    children,
    width = 400,
}) => {
    const theme = useTheme();

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
    }, [open]);

    return (
        <>
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }}
                open={open}
                onClick={onClose}
            />
            <Drawer
                sx={{
                    width: width,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: width,
                        boxSizing: 'border-box',
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        backgroundColor: colors.common.white,
                        zIndex: theme.zIndex.drawer + 2,
                        transition: theme.transitions.create(['transform'], {
                            duration: theme.transitions.duration.standard,
                        }),
                    },
                }}
                variant="temporary"
                anchor="right"
                open={open}
                onClose={onClose}
                ModalProps={{
                    keepMounted: true,
                    style: { zIndex: theme.zIndex.drawer + 2 }
                }}
            >
                <Box sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    {/* Header */}
                    <Box sx={{
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: colors.common.white,
                    }}>
                        <Typography variant="h6">{title}</Typography>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                }
                            }}
                        >
                            <FaXmark />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        px: 0,
                        py: 0,
                        backgroundColor: colors.common.white,
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: theme.palette.background.paper,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.divider,
                            borderRadius: '4px',
                        },
                    }}>
                        {children}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default OffCanvas;
